import { Icon } from '@/common/components/Icon'
import { Button, Modal } from '@/components/ui'
import { cn } from '@/lib/utils'

export interface DeleteConfirmationModalProps {
  showDeleteConfirmationModal: boolean
  setDeleteConfirmationModal: (show: boolean) => void
  deleteFunc: () => void
  cancelText?: boolean
  deleteTitle?: string
  onClose?: () => void
  deletedItem?: string
  size?: 'sm' | 'md' | 'lg'
  centered?: boolean
}

export const DeleteConfirmationModal = (
  props: DeleteConfirmationModalProps,
): JSX.Element => {
  const {
    showDeleteConfirmationModal,
    setDeleteConfirmationModal,
    deleteFunc,
    deleteTitle,
    onClose,
    cancelText = false,
    deletedItem,
    size = 'sm',
    centered = false,
  } = props

  const handleClose = () => {
    setDeleteConfirmationModal(false)
    onClose?.()
  }

  const handleDelete = () => {
    deleteFunc()
    handleClose()
  }

  return (
    <Modal
      size={size}
      headerClassName="mb-[37px]"
      contentClassName="p-0 border-2 border-monochrome-120"
      open={showDeleteConfirmationModal}
      onOpenChange={setDeleteConfirmationModal}
      title={
        <div
          className={cn(
            'flex items-center gap-2.5',
            centered && 'justify-center',
          )}
        >
          <div
            className={cn(
              'h-[44px] w-[44px] rounded-full',
              'bg-monochrome-5 backdrop-blur-md',
              'border-2 border-monochrome-120',
              'flex items-center justify-center',
              'shadow-[0_12px_40px_rgba(0,0,0,0.45)]',
            )}
          >
            <div
              className={cn(
                'h-[40px] w-[40px] rounded-full',
                'shadow-[0_0_24px_rgba(136,100%,51%,0.35)]',
                'flex items-center justify-center',
                'text-accent-1',
              )}
            >
              <Icon
                icon="profile-delete"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              />
            </div>
          </div>
          <h2 className="text-title-xxs text-monochrome-1 font-bold">
            {`${cancelText ? 'Cancel' : 'Delete'} ${deleteTitle}`}{' '}
          </h2>
        </div>
      }
      footerClassName={cn(
        'mt-0 border-t border-monochrome-120 pt-[25px] mt-[25px]',
        centered && '[&_button]:min-w-fit',
      )}
      footer={
        <div
          className={cn(
            'flex w-full items-center gap-[15px]',
            centered ? 'justify-center' : 'justify-end',
          )}
        >
          <Button onClick={handleClose} variant="secondary">
            No
          </Button>
          <Button onClick={handleDelete} variant="destructive">
            Yes, Delete {deleteTitle}
            <Icon
              icon="close-circle"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              className="shrink-0"
            />
          </Button>
        </div>
      }
    >
      <div
        className={`text-monochrome-1 text-title-xxs${
          centered ? ' text-center' : ''
        }`}
      >
        Are you sure you want to {cancelText ? 'cancel' : 'delete'}{' '}
        {deletedItem && (
          <>
            "<span className="text-accent-3">{deletedItem}</span>"
          </>
        )}
        ?
      </div>
    </Modal>
  )
}
